import type { AdminClient } from "../api.js";
import { clear, h, safeText } from "../dom.js";
import type {
  Company,
  ServiceAuthScope,
  StaffRole,
  TokenInfo,
  TokenIssueResponse,
} from "../types.js";
import { card, disclosure, distinctControlNames, emptyState, pageHeader } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import { timeAgoText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { navLabel } from "../ui/sentences.js";
import { forgetPossiblySent, runWrite } from "../ui/write.js";

const CORE_ENTITY_SCOPES = [
  "reports:read",
  "deposits:read",
] as const satisfies readonly ServiceAuthScope[];
const PRACTICE_ENTITY_SCOPES = [
  "reports:read",
  "deposits:read",
  "hub:push",
  "hub_questions:read",
  "hub_questions:resolve",
  "hub_balances:read",
] as const satisfies readonly ServiceAuthScope[];
const HUB_PUSH_NOTE =
  "Only the company that pays practice invoices. The server checks that before saving.";

const SCOPE_FACE: Partial<Record<ServiceAuthScope, string>> = {
  "reports:read": "Read reports",
  "deposits:read": "Read deposits",
  "hub:push": "Send practice totals",
  "hub_questions:read": "Read practice questions",
  "hub_questions:resolve": "Answer practice questions",
  "hub_balances:read": "Read practice balances",
  "admin:queue": "Review questions",
  "admin:rules": "Manage rules",
  "admin:tokens": "Manage access keys",
  "admin:close": "Close a month",
  "admin:health": "See company health",
  "admin:console": "Use the console",
  "admin:onboarding": "Onboarding agent",
};

const PAGE_TITLE = navLabel("tokens");

/**
 * The refusal for a blank or impossible expiry. Shared with the Invitations
 * page (the one other page that issues a dated credential) so the sentence has
 * one home inside group b3, not two copies.
 */
export const INVALID_EXPIRY_COPY = "Enter a valid expiry date and time.";

export interface ScopeChoice {
  readonly scope: ServiceAuthScope;
  readonly checked: boolean;
  readonly note: string | null;
}

/**
 * Checkboxes for the chosen company. Practice pre-selects the practice set.
 * Every other type pre-selects the core pair and offers hub:push unchecked:
 * this page cannot see hub-property status, so the server confirms it.
 * statements:read is not offered until OB-9 stores that option.
 *
 * Exported for the guided-onboarding checklist (OB-3) so the API-key action
 * reuses this policy choice instead of copying it.
 */
export function entityScopeChoices(companyType: string): ScopeChoice[] {
  if (companyType === "gvpm-practice") {
    return PRACTICE_ENTITY_SCOPES.map((scope) => ({ scope, checked: true, note: null }));
  }
  return [
    ...CORE_ENTITY_SCOPES.map((scope) => ({ scope, checked: true, note: null })),
    { scope: "hub:push", checked: false, note: HUB_PUSH_NOTE },
  ];
}

const ADMIN_SCOPES: ServiceAuthScope[] = [
  "admin:queue",
  "admin:rules",
  "admin:tokens",
  "admin:close",
  "admin:health",
  "admin:console",
];

/**
 * Display labels for scopes a credential may carry but this page must not
 * offer to issue. `admin:onboarding` belongs to the named agent identity and
 * is refused (422) by the issue route.
 */
const SCOPE_LABELS: Partial<Record<ServiceAuthScope, string>> = {
  "admin:onboarding": "Onboarding agent",
};

export function scopeLabel(scope: ServiceAuthScope): string {
  const label = SCOPE_LABELS[scope];
  return label === undefined ? scope : `${scope} (${label})`;
}

function scopeFace(scope: ServiceAuthScope): string {
  return SCOPE_FACE[scope] ?? "Listed permission";
}

export interface TokensPageOptions {
  client: AdminClient;
  root: HTMLElement;
  /** Who is reading; the shell passes it where it knows (optional). */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
}

/** List/issue/revoke tokens; show company↔hub mapping. Plaintext shown once. */
export async function renderTokensPage(opts: TokensPageOptions): Promise<void> {
  const { client, root } = opts;
  const role = readerRole(opts.role);
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);
  if (role === "account_manager") {
    page.append(
      pageHeader(PAGE_TITLE, "Only an owner can create or revoke access keys."),
      emptyState(advice("view_access_keys", role)),
    );
    return;
  }
  page.append(h("p", { class: "muted" }, ["Loading access keys…"]));

  const [tokenData, companyData] = await Promise.all([
    client.listTokens(),
    client.listCompanies(),
  ]);

  if (!(opts.isCurrent?.() ?? true)) return;
  clear(page);
  page.append(
      pageHeader(
      PAGE_TITLE,
      "Create a key for another system. The secret is shown once. After that, only the name remains.",
    ),
  );

  const onceBanner = h("div", {
    class: "once-banner",
    "data-role": "token-once",
    hidden: "true",
  }, []);
  /**
   * A failed second Issue must not replace the banner that is still showing an
   * earlier key's one-time secret. When a secret is on screen the failure goes
   * here, beside it, and the banner keeps the secret (rd round 11 review,
   * SHOULD-FIX 8).
   */
  const issueError = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "token-issue-error",
    hidden: "true",
  }, []);
  page.append(onceBanner, issueError);
  /**
   * The id whose plaintext the banner is showing, or null. Revoking THAT key
   * takes its secret off the screen; revoking any other key must leave it
   * alone (rd round 11, R8-08).
   */
  let shownSecretId: string | null = null;
  const hideIssueError = (): void => {
    clear(issueError);
    issueError.hidden = true;
    issueError.setAttribute("hidden", "true");
  };
  const hideOnce = (): void => {
    clear(onceBanner);
    onceBanner.hidden = true;
    onceBanner.setAttribute("hidden", "true");
    onceBanner.className = "once-banner";
    shownSecretId = null;
    // The note was about a failed Issue that sat beside this secret; it goes
    // when the secret goes (rd round 11 review, SHOULD-FIX 7).
    hideIssueError();
  };
  const onRevoked = (id: string): void => {
    if (shownSecretId === id) hideOnce();
  };
  /**
   * Where an Issue failure/validation message goes. While a secret is visible
   * it goes beside it; with no secret on screen the once banner is free to
   * carry the message, and its id tracking is cleared with it.
   */
  const showIssueError = (message: string, detail?: string): void => {
    if (shownSecretId !== null) {
      showFieldError(issueError, message, detail, "banner banner-err");
      return;
    }
    shownSecretId = null;
    showFieldError(onceBanner, message, detail);
  };

  const directory = face.createCompanyDirectory(
    companyData.companies.map((row) => ({ slug: row.slug, displayName: row.displayName })),
    { complete: true },
  );
  page.append(h("h2", {}, ["Companies"]));
  page.append(card(h("div", { class: "table-scroll" }, [renderCompanyTable(companyData.companies, directory)])));

  page.append(h("h2", {}, ["Issued keys"]));
  const revokeBanner = h("p", {
    class: "banner",
    "data-role": "token-revoke",
    hidden: "true",
  }, []);
  page.append(revokeBanner);
  const list = h("ul", { class: "token-list", "data-role": "token-list" });
  /** The keys this page already knows about: a new id is a key that landed. */
  const knownIds = new Set(tokenData.tokens.map((token) => token.id));
  for (const token of tokenData.tokens) {
    list.append(renderTokenRow(token, client, list, revokeBanner, directory, role, onRevoked));
  }
  // Two keys for one company expiring the same day read identically: tell them
  // apart by position, never by id (round 3).
  const nameRevokes = (): void => {
    distinctControlNames(list, '[data-action="revoke-token"]');
  };
  nameRevokes();
  if (tokenData.tokens.length === 0) {
    list.append(
      h("li", { class: "empty", "data-role": "tokens-empty" }, [
        "No access keys yet. Create one below when you are ready to connect another system.",
      ]),
    );
  }
  page.append(list);

  page.append(h("h2", {}, ["Create a key"]));
  const companySelect = h("select", { "aria-label": "Company" });
  for (const company of companyData.companies) {
    companySelect.append(
      h("option", { value: company.slug }, [face.company(company.slug, directory, "sentence")]),
    );
  }
  const kindSelect = h("select", { "aria-label": "Kind of key" }, [
    h("option", { value: "entity" }, ["For one company"]),
    h("option", { value: "admin" }, ["For administration"]),
  ]);
  const labelInput = h("input", {
    type: "text",
    placeholder: "Label (optional)",
    "aria-label": "Label",
  });
  const expiresInput = h("input", {
    type: "datetime-local",
    "aria-label": "Expires at",
  });
  // Default: 90 days out
  const defaultExpiry = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
  let seededExpiry = toLocalInput(defaultExpiry);
  expiresInput.value = seededExpiry;
  /** True once the person edits the field; an untouched default is re-seeded. */
  let expiryTouched = false;
  expiresInput.addEventListener("input", () => {
    expiryTouched = true;
  });

  const scopeHost = h("div", { "data-role": "entity-scope-choices" });

  function selectedCompany(): Company | undefined {
    return companyData.companies.find((company) => company.slug === companySelect.value);
  }

  function syncScopeChoices(): void {
    clear(scopeHost);
    if (kindSelect.value === "admin") {
      // R8-20a: an administration key used to show no permissions at all. The
      // owner is granting this set, so say it in the same words the list uses.
      scopeHost.hidden = false;
      scopeHost.removeAttribute("hidden");
      scopeHost.append(
        h("p", { class: "muted", "data-role": "admin-scope-list" }, [
          `An administration key can ${ADMIN_SCOPES.map(scopeFace).join(", ")}.`,
        ]),
      );
      return;
    }
    scopeHost.hidden = false;
    scopeHost.removeAttribute("hidden");
    for (const choice of entityScopeChoices(selectedCompany()?.companyType ?? "")) {
      const input = h("input", {
        type: "checkbox",
        "data-scope": choice.scope,
        "aria-label": face.ariaFor(scopeFace(choice.scope)),
      });
      input.checked = choice.checked;
      const caption = choice.note === null ? scopeFace(choice.scope) : `${scopeFace(choice.scope)}. ${choice.note}`;
      scopeHost.append(
        h("label", {}, [
          input,
          caption,
          disclosure("Show details", h("p", {}, [choice.scope])),
        ]),
      );
    }
  }

  companySelect.addEventListener("change", () => {
    syncScopeChoices();
  });
  kindSelect.addEventListener("change", () => {
    syncScopeChoices();
  });
  syncScopeChoices();

  const issueBtn = h(
    "button",
    { type: "button", class: "btn btn-primary", "aria-label": "Issue key" },
    ["Issue"],
  );

  /**
   * A response we could not read may still have minted a key. The stable key
   * and `blockWhenPossiblySent` make a second press send nothing; this button
   * reads the page's own list and either adopts the key that exists or clears
   * the memory so a genuine retry is possible (item B3b-3).
   */
  const offerKeyCheck = (key: string): HTMLElement => {
    const check = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "check-access-keys" },
      ["Check access keys"],
    );
    check.addEventListener("click", () => {
      void (async () => {
        check.disabled = true;
        let tokens: TokenInfo[];
        try {
          tokens = (await client.listTokens()).tokens;
        } catch (error) {
          check.disabled = false;
          showIssueError(failureCopy(error, { role, context: "read" }).sentence);
          return;
        }
        const landed = tokens.find((token) => !knownIds.has(token.id));
        forgetPossiblySent(key);
        if (landed === undefined) {
          showIssueError(
            outcomeSentence({ kind: "refused", status: 0 }, role),
          );
          return;
        }
        knownIds.add(landed.id);
        list.querySelector('[data-role="tokens-empty"]')?.remove();
        list.prepend(renderTokenRow(landed, client, list, revokeBanner, directory, role, onRevoked));
        nameRevokes();
        showIssueError(
          "The key was created, but its secret cannot be shown again. Revoke it and create a new one if you did not copy it.",
        );
      })();
    });
    return check;
  };

  issueBtn.addEventListener("click", () => {
    void (async () => {
      const company = companySelect.value;
      if (!company) return;
      // A page left open past its untouched default must not send a stale
      // value the Worker refuses (round 2 M9). A field the person cleared or
      // edited is theirs: only the exact seeded value is re-seeded.
      if (!expiryTouched && expiresInput.value === seededExpiry) {
        seededExpiry = toLocalInput(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000));
        expiresInput.value = seededExpiry;
      }
      const expiresAt = parseExpiresAt(expiresInput.value);
      if (expiresAt === null) {
        showIssueError(INVALID_EXPIRY_COPY);
        return;
      }
      const scopes =
        kindSelect.value === "admin" ? [...ADMIN_SCOPES] : selectedEntityScopes(scopeHost);
      if (scopes.length === 0) {
        showIssueError("Choose at least one permission.");
        return;
      }
      const key = `token-issue:${company}`;
      const held: { issued: TokenIssueResponse | undefined } = { issued: undefined };
      const outcome = await runWrite({
        key,
        // Only the issue form goes busy: the key list (and its revoke controls)
        // stays usable while a request is out.
        scope: issueBtn.closest(".card") ?? page,
        gate: () => null,
        send: () =>
          client.issueToken({
            company,
            scopes,
            expiresAt,
            ...(labelInput.value.trim() ? { label: labelInput.value.trim() } : {}),
          }),
        // A lost answer can mean the key exists: look for an id this page did
        // not already hold before claiming it landed.
        readBack: async () => {
          const { tokens } = await client.listTokens();
          const found = tokens.find((token) => !knownIds.has(token.id));
          return found !== undefined;
        },
        afterLanded: (value) => {
          held.issued = value;
        },
        stillCurrent: () => true,
        blockWhenPossiblySent: true,
      });
      if (outcome.kind === "landed") {
        if (held.issued === undefined) {
          // The read-back found the key, but the response (and its secret) is
          // gone. Say exactly that instead of pretending nothing happened.
          showIssueError(
            "The key was created, but its secret cannot be shown again. Revoke it and create a new one if you did not copy it.",
          );
          return;
        }
        const created: TokenIssueResponse = held.issued;
        knownIds.add(created.id);
        hideIssueError();
        showOnce(onceBanner, created.token, created.id);
        shownSecretId = created.id;
        list.querySelector('[data-role="tokens-empty"]')?.remove();
        list.prepend(
          renderTokenRow(
            {
              id: created.id,
              company: created.company,
              scopes: created.scopes as TokenInfo["scopes"],
              label: created.label ?? null,
              createdAt: new Date().toISOString(),
              expiresAt: created.expiresAt,
              revokedAt: null,
            },
            client,
            list,
            revokeBanner,
            directory,
            role,
            onRevoked,
          ),
        );
        nameRevokes();
        return;
      }
      // Whatever the error says, "nothing changed" is not knowable here: the
      // request may have reached the Worker. An unknown answer reads the
      // possibly-sent sentence (round 2 M1): never "refresh", because a reload
      // forgets the possibly-sent mark and can mint a second key. The person
      // settles it with the read below.
      const copy =
        outcome.kind === "unknown"
          ? {
              sentence: outcomeSentence({ kind: "unknown", blocked: true }, role),
              detail:
                outcome.error === undefined
                  ? null
                  : failureCopy(outcome.error, { role, context: "write" }).detail,
            }
          : outcome.error === undefined
            ? { sentence: outcomeSentence(outcome, role), detail: null }
            : failureCopy(outcome.error, { role, context: "write" });
      showIssueError(copy.sentence, copy.detail ?? undefined);
      const host = shownSecretId !== null ? issueError : onceBanner;
      host.append(offerKeyCheck(key));
    })();
  });

  page.append(
    card(
    h("div", { class: "form-row" }, [
      h("label", {}, ["Company", companySelect]),
      h("label", {}, ["Kind", kindSelect]),
      h("label", {}, ["Label", labelInput]),
      h("label", {}, ["Expires", expiresInput]),
      scopeHost,
      issueBtn,
    ]),
    ),
  );
}

function selectedEntityScopes(scopeHost: HTMLElement): ServiceAuthScope[] {
  const scopes: ServiceAuthScope[] = [];
  for (const input of scopeHost.querySelectorAll<HTMLInputElement>("input[data-scope]")) {
    if (!input.checked) continue;
    const scope = input.getAttribute("data-scope");
    if (scope) scopes.push(scope as ServiceAuthScope);
  }
  return scopes;
}

function renderCompanyTable(
  companies: Company[],
  directory: face.CompanyDirectory,
): HTMLElement {
  const table = h("table", { class: "data-table" });
  const thead = h("thead", {}, [
    h("tr", {}, [
      h("th", {}, ["Company"]),
      h("th", {}, ["Kind"]),
      h("th", {}, ["Practice link"]),
      h("th", {}, ["In use"]),
    ]),
  ]);
  const tbody = h("tbody", {});
  for (const company of companies) {
    tbody.append(
      h("tr", { "data-company": company.slug }, [
        h("td", {}, [
          face.company(company.slug, directory, "sentence"),
          // A slug is an internal key: it stays inside Show details.
          disclosure("Show details", h("p", {}, [company.slug])),
        ]),
        h("td", {}, [
          companyKind(company.companyType),
          disclosure("Show details", h("p", {}, [company.companyType])),
        ]),
        h("td", {}, [
          // The practice link is a connection id (`hub-practice-...`): the
          // owner needs to know one exists, not to read the key.
          company.hubLink === null ? "(none)" : "Linked",
          ...(company.hubLink === null
            ? []
            : [disclosure("Show details", h("p", {}, [safeText(company.hubLink, "(none)")]))]),
        ]),
        h("td", {}, [company.isActive ? "yes" : "no"]),
      ]),
    );
  }
  table.append(thead, tbody);
  return table;
}

function renderTokenRow(
  token: TokenInfo,
  client: AdminClient,
  list: HTMLElement,
  revokeBanner: HTMLElement,
  directory: face.CompanyDirectory,
  role: StaffRole,
  onRevoked?: (id: string) => void,
): HTMLLIElement {
  const revoked = token.revokedAt !== null;
  // R8-20a: a key past its expiry is not "expires 3 days ago" — it is expired.
  const expired = !revoked && new Date(token.expiresAt).getTime() < Date.now();
  const row = h("li", {
    class: revoked ? "token-row revoked" : expired ? "token-row expired" : "token-row",
    "data-token-id": token.id,
  }, [
    h("div", {}, [
      h("strong", {}, [
        token.label ?? `${face.company(token.company, directory, "sentence")} key`,
      ]),
      h("span", { class: "muted" }, [
        ` · ${face.company(token.company, directory, "sentence")} · ${token.scopes.map(scopeFace).join(", ")} · ${expired ? "expired" : "expires"} ${formatWhen(token.expiresAt)}`,
      ]),
      revoked ? h("span", { class: "badge" }, [" revoked"]) : null,
      expired ? h("span", { class: "badge", "data-role": "token-expired" }, [" expired"]) : null,
      disclosure(
        "Show details",
        h("p", {}, [
          `${token.id} · ${token.scopes.map(scopeLabel).join(", ")} · ${token.expiresAt}`,
        ]),
      ),
    ]),
  ]);

  if (!revoked) {
    const revokeBtn = h("button", {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "revoke-token",
      // A person reads the appearance, never the key id (rc-3 rows 18-19).
      // The visible word stays "Revoke"; the accessible name says which key,
      // so two keys on one page are told apart (round 2 M8).
      "aria-label": face.ariaFor(
        `Revoke the key for ${face.company(token.company, directory, "sentence")}, ${expired ? "expired" : "expires"} ${formatWhen(token.expiresAt)}`,
      ),
    }, ["Revoke"]);
    revokeBtn.addEventListener("click", () => {
      void (async () => {
        revokeBtn.disabled = true;
        const heldUpdated: { value: TokenInfo | undefined } = { value: undefined };
        const outcome = await runWrite({
          key: `token-revoke:${token.id}`,
          scope: row,
          gate: () => null,
          send: () => client.revokeToken(token.id),
          afterLanded: (value) => {
            heldUpdated.value = value;
          },
          stillCurrent: () => true,
        });
        if (outcome.kind === "landed") {
          if (heldUpdated.value === undefined) return;
          const fresh: TokenInfo = heldUpdated.value;
          clear(revokeBanner);
          revokeBanner.hidden = true;
          revokeBanner.setAttribute("hidden", "true");
          revokeBanner.className = "banner";
          row.replaceWith(
            renderTokenRow(fresh, client, list, revokeBanner, directory, role, onRevoked),
          );
          // A dead key's secret must not stay on screen (R8-08).
          onRevoked?.(token.id);
          return;
        }
        clear(revokeBanner);
        revokeBanner.hidden = false;
        revokeBanner.removeAttribute("hidden");
        revokeBanner.className = "banner banner-err";
        const copy = failureCopy(outcome.error, { role, context: "write" });
        revokeBanner.append(document.createTextNode(copy.sentence));
        if (copy.detail !== null) {
          revokeBanner.append(disclosure("Show details", h("p", {}, [copy.detail])));
        }
        revokeBtn.disabled = false;
      })();
    });
    row.append(revokeBtn);
  }
  return row;
}

function showOnce(el: HTMLElement, plaintext: string, id: string): void {
  clear(el);
  el.hidden = false;
  el.removeAttribute("hidden");
  el.className = "once-banner banner-ok";
  el.append(
    h("p", {}, ["Copy this secret now. It will not be shown again."]),
    h("code", { "data-role": "plaintext-token", "data-token-id": id }, [plaintext]),
  );
}

/** Guard empty/invalid expiry before `toISOString()` (which throws on Invalid Date). */
function parseExpiresAt(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  // A date at or before now is not a lifetime; the Worker refuses it and the
  // person would be stuck resending the same value (round 2 M9).
  if (parsed.getTime() <= Date.now()) return null;
  return parsed.toISOString();
}

function showFieldError(
  el: HTMLElement,
  message: string,
  detail?: string,
  className = "once-banner banner-err",
): void {
  clear(el);
  el.hidden = false;
  el.removeAttribute("hidden");
  el.className = className;
  el.append(document.createTextNode(message));
  if (detail !== undefined && detail !== "" && detail !== message) {
    el.append(disclosure("Show details", h("p", {}, [detail])));
  }
}

function companyKind(companyType: string): string {
  if (companyType === "gvpm-practice") return "Practice";
  if (companyType === "gv-internal") return "Internal";
  if (companyType === "property" || companyType === "rental") return "Property";
  return "Company";
}

function formatWhen(iso: string): string {
  const phrase = timeAgoText(iso);
  return phrase === "" ? "an unknown time" : phrase;
}

function toLocalInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
